import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { ProgressSpinner } from 'primereact/progressspinner';
import { useNavigate } from "react-router-dom";
import { Routes } from "constants/routes";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Input } from "ui-kit/PrimitiveControls/Input";
import { CreateReleaseButton } from "ui-kit/Button";
import { Filters } from "./Filters";
import { useReleases, IFilters } from "./hooks/useReleases";
import { Statusbar } from './Statusbar';
import { TableView } from './TableView';
import { ListView } from './ListView';
import { TileView } from './TileView';
import { View } from './constants';

export const Releases = () => {
  const [selected, setSelected] = useState([]);
  const viewStorageKey = "releases:view";
  const pageSizeStorageKey = "releases:pageSize";
  const [view, setView] = useState<View>(() => {
    const raw = localStorage.getItem(viewStorageKey);
    if (raw === View.Grid || raw === View.List || raw === View.Tile) return raw as View;
    return View.Grid;
  });
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [first, setFirst] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(() => {
    const raw = localStorage.getItem(pageSizeStorageKey);
    const n = raw ? Number(raw) : NaN;
    const allowed = [5, 10, 20, 30];
    if (Number.isFinite(n) && allowed.includes(n)) return n;
    return 30;
  });
  const [filterArtistIds, setFilterArtistIds] = useState<string[]>([]);
  const [filterContributorIds, setFilterContributorIds] = useState<string[]>([]);
  const [filterLabelIds, setFilterLabelIds] = useState<string[]>([]);
  const [filterPlatformIds, setFilterPlatformIds] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 640px)").matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setIsMobile(mq.matches);
    update();

    // Safari fallback
    if ((mq as any).addEventListener) {
      const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
      (mq as any).addEventListener("change", handler);
      return () => (mq as any).removeEventListener("change", handler);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);
  const { releases, fetching, total } = useReleases(
    selectedStatus,
    currentPage,
    pageCount,
    filterArtistIds,
    filterPlatformIds,
    filterLabelIds,
    filterContributorIds
  );
  
  const searchedReleases = useMemo(() => {
    // Apply type filter only for supported types; treat "All" or empty as no filter.
    const normalizedType = String(selectedType || "").toUpperCase();
    const validTypes = new Set(["SINGLE", "EP", "ALBUM"]);

    const byType = validTypes.has(normalizedType)
      ? releases.filter((r) => {
          const rt = String((r as any)?.release_type ?? "").toUpperCase();
          return rt === normalizedType;
        })
      : releases;

    return search
      ? byType.filter((release) => release.title.toLowerCase().includes(search.toLowerCase()))
      : byType;
  }, [releases, search, selectedType]);

  const visibleCount = searchedReleases.length;

  const onStatusClick = React.useCallback((status: string) => setSelectedStatus(status), []);
  const onTypeClick = React.useCallback((type: string) => setSelectedType(type), []);

  const [controlsHidden, setControlsHidden] = useState(false);
  const lastScrollTopRef = React.useRef(0);
  const rafRef = React.useRef<number | null>(null);
  const controlsHiddenRef = React.useRef(false);

  React.useEffect(() => {
    controlsHiddenRef.current = controlsHidden;
  }, [controlsHidden]);

  React.useEffect(() => {
    localStorage.setItem(viewStorageKey, view);
  }, [view]);

  React.useEffect(() => {
    localStorage.setItem(pageSizeStorageKey, String(pageCount));
  }, [pageCount]);

  // On mobile we don't allow Tile view: auto-fallback if it was saved previously.
  React.useEffect(() => {
    if (!isMobile) return;
    if (view === View.Tile) setView(View.Grid);
  }, [isMobile, view]);

  const onAddNewRelease = () => {
    localStorage.setItem("newReleaseFrom", "releases");
    navigate(Routes.NewRelease);
  };

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setCurrentPage(event.page + 1);
    setFirst(event.rows * event.page);
    setPageCount(event.rows);
  };

  const onSelectFilter = (filter: IFilters) => {
    filter.filterArtistIds && setFilterArtistIds(filter.filterArtistIds);
    filter.filterPlatformIds && setFilterPlatformIds(filter.filterPlatformIds);
    filter.filterLabelIds && setFilterLabelIds(filter.filterLabelIds);
    filter.filterContributorIds && setFilterContributorIds(filter.filterContributorIds);
  };

  const onResetFilters = () => {
    setFilterArtistIds([]);
    setFilterPlatformIds([]);
    setFilterLabelIds([]);
    setFilterContributorIds([]);
    setSelectedType('');
    setSearch('');
    setCurrentPage(1);
    setFirst(0);
  };

  const onListScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nextScrollTop = el.scrollTop;

    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const prev = lastScrollTopRef.current;
      const delta = nextScrollTop - prev;
      lastScrollTopRef.current = nextScrollTop;

      // Always show near the top
      if (nextScrollTop < 16) {
        if (controlsHiddenRef.current) setControlsHidden(false);
        return;
      }

      // Hide when scrolling down, show when scrolling up
      if (delta > 10 && nextScrollTop > 60) {
        if (!controlsHiddenRef.current) setControlsHidden(true);
      } else if (delta < -10) {
        if (controlsHiddenRef.current) setControlsHidden(false);
      }
    });
  }, []);
  

  return (
    <Container>
      <Statusbar
        selectedStatus={selectedStatus}
        onStatusClick={onStatusClick}
        view={view}
        onChangeView={(view: View) => setView(view)}
        isMobile={isMobile}
      />
      <ControlsBar $hidden={controlsHidden}>
        <TitleFiltersRow>
          <Title>{total} releases</Title>
          <SearchInput
            name="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search"
          />
          <FiltersWrapper>
            <Filters
              selectedType={selectedType}
              onTypeChange={setSelectedType}
              filterArtistIds={filterArtistIds}
              filterPlatformIds={filterPlatformIds}
              filterLabelIds={filterLabelIds}
              filterContributorIds={filterContributorIds}
              onSelectFilter={onSelectFilter}
              onResetFilters={onResetFilters}
            />
          </FiltersWrapper>
        </TitleFiltersRow>
      </ControlsBar>
      <ItemsContainer>
        {fetching ? (
          <Loader>
            <ProgressSpinner />
          </Loader>
        ) : searchedReleases.length ? (
          <>
            <DataContainer onScroll={onListScroll}>
            {view === View.Grid && (
              <TableView
                currentPage={first}
                pageCount={pageCount}
                total={total}
                data={searchedReleases}
                selected={selected}
                onSelectionChange={setSelected}
              />
            )}
            {view === View.List && (
              <ListView
                currentPage={first}
                pageCount={pageCount}
                total={total}
                data={searchedReleases}
              />
            )}
            {view === View.Tile && (
              <TileView
                currentPage={first}
                pageCount={pageCount}
                total={total}
                data={searchedReleases}
              />
            )}
            <BottomSpacer />
            </DataContainer>
           
          </>
        ) : (
          <NoReleases>
            <NoReleasesTitle>No releases yet</NoReleasesTitle>
            <AddNewReleaseButton onClick={onAddNewRelease}>
              <svg width="181" height="47" viewBox="0 0 181 47" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="181" height="47" rx="10" fill="#6777FF"/>
                <path d="M46.4301 29.16C45.6301 29.16 44.8888 29.016 44.2061 28.728C43.5235 28.4293 42.9261 28.0187 42.4141 27.496C41.9128 26.9733 41.5235 26.3653 41.2461 25.672C40.9688 24.9787 40.8301 24.2373 40.8301 23.448C40.8301 22.648 40.9688 21.9013 41.2461 21.208C41.5235 20.5147 41.9128 19.912 42.4141 19.4C42.9261 18.8773 43.5181 18.472 44.1901 18.184C44.8728 17.8853 45.6141 17.736 46.4141 17.736C47.2888 17.736 48.0568 17.8853 48.7181 18.184C49.3795 18.4827 49.9661 18.888 50.4781 19.4L49.4381 20.44C49.0861 20.0453 48.6541 19.736 48.1421 19.512C47.6408 19.288 47.0648 19.176 46.4141 19.176C45.8275 19.176 45.2888 19.2827 44.7981 19.496C44.3075 19.6987 43.8808 19.992 43.5181 20.376C43.1555 20.76 42.8728 21.2133 42.6701 21.736C42.4781 22.2587 42.3821 22.8293 42.3821 23.448C42.3821 24.0667 42.4781 24.6373 42.6701 25.16C42.8728 25.6827 43.1555 26.136 43.5181 26.52C43.8808 26.904 44.3075 27.2027 44.7981 27.416C45.2888 27.6187 45.8275 27.72 46.4141 27.72C47.1075 27.72 47.7101 27.608 48.2221 27.384C48.7341 27.1493 49.1661 26.8347 49.5181 26.44L50.5581 27.464C50.0461 27.9973 49.4435 28.4133 48.7501 28.712C48.0675 29.0107 47.2941 29.16 46.4301 29.16ZM52.1497 29V21.4H53.5898V29H52.1497ZM53.5898 24.664L53.0458 24.424C53.0458 23.4533 53.2698 22.68 53.7178 22.104C54.1658 21.528 54.8111 21.24 55.6538 21.24C56.0378 21.24 56.3844 21.3093 56.6938 21.448C57.0031 21.576 57.2911 21.7947 57.5578 22.104L56.6138 23.08C56.4538 22.9093 56.2778 22.7867 56.0858 22.712C55.8938 22.6373 55.6698 22.6 55.4138 22.6C54.8804 22.6 54.4431 22.7707 54.1018 23.112C53.7604 23.4533 53.5898 23.9707 53.5898 24.664ZM61.8416 29.16C61.0843 29.16 60.4016 28.9893 59.7936 28.648C59.1856 28.296 58.7056 27.8213 58.3536 27.224C58.0016 26.6267 57.8256 25.9493 57.8256 25.192C57.8256 24.4453 57.9963 23.7733 58.3376 23.176C58.6896 22.5787 59.159 22.1093 59.7456 21.768C60.343 21.416 61.0096 21.24 61.7456 21.24C62.4496 21.24 63.0683 21.4 63.6016 21.72C64.1456 22.04 64.567 22.4827 64.8656 23.048C65.175 23.6133 65.3296 24.2533 65.3296 24.968C65.3296 25.0747 65.3243 25.192 65.3136 25.32C65.303 25.4373 65.2816 25.576 65.2496 25.736H58.8336V24.536H64.4816L63.9536 25C63.9536 24.488 63.863 24.056 63.6816 23.704C63.5003 23.3413 63.2443 23.064 62.9136 22.872C62.583 22.6693 62.183 22.568 61.7136 22.568C61.223 22.568 60.791 22.6747 60.4176 22.888C60.0443 23.1013 59.7563 23.4 59.5536 23.784C59.351 24.168 59.2496 24.6213 59.2496 25.144C59.2496 25.6773 59.3563 26.1467 59.5696 26.552C59.783 26.9467 60.087 27.256 60.4816 27.48C60.8763 27.6933 61.3296 27.8 61.8416 27.8C62.2683 27.8 62.6576 27.7253 63.0096 27.576C63.3723 27.4267 63.6816 27.2027 63.9376 26.904L64.8656 27.848C64.503 28.2747 64.055 28.6 63.5216 28.824C62.999 29.048 62.439 29.16 61.8416 29.16ZM70.1318 29.16C69.4384 29.16 68.8091 28.9893 68.2438 28.648C67.6784 28.296 67.2304 27.8213 66.8997 27.224C66.5798 26.6267 66.4198 25.9547 66.4198 25.208C66.4198 24.4613 66.5798 23.7893 66.8997 23.192C67.2304 22.5947 67.6731 22.12 68.2278 21.768C68.7931 21.416 69.4278 21.24 70.1318 21.24C70.7078 21.24 71.2198 21.3627 71.6678 21.608C72.1264 21.8427 72.4944 22.1733 72.7718 22.6C73.0491 23.016 73.2038 23.5013 73.2358 24.056V26.344C73.2038 26.888 73.0491 27.3733 72.7718 27.8C72.5051 28.2267 72.1424 28.5627 71.6838 28.808C71.2358 29.0427 70.7184 29.16 70.1318 29.16ZM70.3718 27.8C71.0864 27.8 71.6624 27.56 72.0998 27.08C72.5371 26.5893 72.7558 25.9653 72.7558 25.208C72.7558 24.6853 72.6544 24.232 72.4518 23.848C72.2598 23.4533 71.9824 23.1493 71.6198 22.936C71.2571 22.712 70.8358 22.6 70.3558 22.6C69.8758 22.6 69.4491 22.712 69.0758 22.936C68.7131 23.16 68.4251 23.4693 68.2118 23.864C68.0091 24.248 67.9078 24.6907 67.9078 25.192C67.9078 25.704 68.0091 26.1573 68.2118 26.552C68.4251 26.936 68.7184 27.24 69.0918 27.464C69.4651 27.688 69.8918 27.8 70.3718 27.8ZM72.6598 29V26.952L72.9318 25.096L72.6598 23.256V21.4H74.1158V29H72.6598ZM77.349 29V18.216H78.789V29H77.349ZM75.477 22.712V21.4H80.661V22.712H75.477ZM85.3573 29.16C84.5999 29.16 83.9173 28.9893 83.3093 28.648C82.7013 28.296 82.2213 27.8213 81.8693 27.224C81.5173 26.6267 81.3413 25.9493 81.3413 25.192C81.3413 24.4453 81.5119 23.7733 81.8533 23.176C82.2053 22.5787 82.6746 22.1093 83.2613 21.768C83.8586 21.416 84.5253 21.24 85.2613 21.24C85.9653 21.24 86.5839 21.4 87.1173 21.72C87.6613 22.04 88.0826 22.4827 88.3813 23.048C88.6906 23.6133 88.8453 24.2533 88.8453 24.968C88.8453 25.0747 88.8399 25.192 88.8293 25.32C88.8186 25.4373 88.7973 25.576 88.7653 25.736H82.3493V24.536H87.9973L87.4693 25C87.4693 24.488 87.3786 24.056 87.1973 23.704C87.0159 23.3413 86.7599 23.064 86.4293 22.872C86.0986 22.6693 85.6986 22.568 85.2293 22.568C84.7386 22.568 84.3066 22.6747 83.9333 22.888C83.5599 23.1013 83.2719 23.4 83.0693 23.784C82.8666 24.168 82.7653 24.6213 82.7653 25.144C82.7653 25.6773 82.8719 26.1467 83.0853 26.552C83.2986 26.9467 83.6026 27.256 83.9973 27.48C84.3919 27.6933 84.8453 27.8 85.3573 27.8C85.7839 27.8 86.1733 27.7253 86.5253 27.576C86.8879 27.4267 87.1973 27.2027 87.4533 26.904L88.3813 27.848C88.0186 28.2747 87.5706 28.6 87.0373 28.824C86.5146 29.048 85.9546 29.16 85.3573 29.16ZM96.9755 29.16C96.2822 29.16 95.6528 28.9893 95.0875 28.648C94.5222 28.296 94.0742 27.8213 93.7435 27.224C93.4235 26.6267 93.2635 25.9547 93.2635 25.208C93.2635 24.4613 93.4235 23.7893 93.7435 23.192C94.0742 22.5947 94.5168 22.12 95.0715 21.768C95.6368 21.416 96.2715 21.24 96.9755 21.24C97.5515 21.24 98.0635 21.3627 98.5115 21.608C98.9702 21.8427 99.3382 22.1733 99.6155 22.6C99.8928 23.016 100.048 23.5013 100.08 24.056V26.344C100.048 26.888 99.8928 27.3733 99.6155 27.8C99.3488 28.2267 98.9862 28.5627 98.5275 28.808C98.0795 29.0427 97.5622 29.16 96.9755 29.16ZM97.2155 27.8C97.9302 27.8 98.5062 27.56 98.9435 27.08C99.3808 26.5893 99.5995 25.9653 99.5995 25.208C99.5995 24.6853 99.4982 24.232 99.2955 23.848C99.1035 23.4533 98.8262 23.1493 98.4635 22.936C98.1008 22.712 97.6795 22.6 97.1995 22.6C96.7195 22.6 96.2928 22.712 95.9195 22.936C95.5568 23.16 95.2688 23.4693 95.0555 23.864C94.8528 24.248 94.7515 24.6907 94.7515 25.192C94.7515 25.704 94.8528 26.1573 95.0555 26.552C95.2688 26.936 95.5622 27.24 95.9355 27.464C96.3088 27.688 96.7355 27.8 97.2155 27.8ZM99.5035 29V26.952L99.7755 25.096L99.5035 23.256V21.4H100.96V29H99.5035ZM106.384 29V21.4H107.824V29H106.384ZM107.824 24.664L107.28 24.424C107.28 23.4533 107.504 22.68 107.952 22.104C108.4 21.528 109.045 21.24 109.888 21.24C110.272 21.24 110.619 21.3093 110.928 21.448C111.237 21.576 111.525 21.7947 111.792 22.104L110.848 23.08C110.688 22.9093 110.512 22.7867 110.32 22.712C110.128 22.6373 109.904 22.6 109.648 22.6C109.115 22.6 108.677 22.7707 108.336 23.112C107.995 23.4533 107.824 23.9707 107.824 24.664ZM116.076 29.16C115.319 29.16 114.636 28.9893 114.028 28.648C113.42 28.296 112.94 27.8213 112.588 27.224C112.236 26.6267 112.06 25.9493 112.06 25.192C112.06 24.4453 112.231 23.7733 112.572 23.176C112.924 22.5787 113.393 22.1093 113.98 21.768C114.577 21.416 115.244 21.24 115.98 21.24C116.684 21.24 117.303 21.4 117.836 21.72C118.38 22.04 118.801 22.4827 119.1 23.048C119.409 23.6133 119.564 24.2533 119.564 24.968C119.564 25.0747 119.559 25.192 119.548 25.32C119.537 25.4373 119.516 25.576 119.484 25.736H113.068V24.536H118.716L118.188 25C118.188 24.488 118.097 24.056 117.916 23.704C117.735 23.3413 117.479 23.064 117.148 22.872C116.817 22.6693 116.417 22.568 115.948 22.568C115.457 22.568 115.025 22.6747 114.652 22.888C114.279 23.1013 113.991 23.4 113.788 23.784C113.585 24.168 113.484 24.6213 113.484 25.144C113.484 25.6773 113.591 26.1467 113.804 26.552C114.017 26.9467 114.321 27.256 114.716 27.48C115.111 27.6933 115.564 27.8 116.076 27.8C116.503 27.8 116.892 27.7253 117.244 27.576C117.607 27.4267 117.916 27.2027 118.172 26.904L119.1 27.848C118.737 28.2747 118.289 28.6 117.756 28.824C117.233 29.048 116.673 29.16 116.076 29.16ZM121.134 29V17.576H122.574V29H121.134ZM128.201 29.16C127.444 29.16 126.761 28.9893 126.153 28.648C125.545 28.296 125.065 27.8213 124.713 27.224C124.361 26.6267 124.185 25.9493 124.185 25.192C124.185 24.4453 124.356 23.7733 124.697 23.176C125.049 22.5787 125.518 22.1093 126.105 21.768C126.702 21.416 127.369 21.24 128.105 21.24C128.809 21.24 129.428 21.4 129.961 21.72C130.505 22.04 130.926 22.4827 131.225 23.048C131.534 23.6133 131.689 24.2533 131.689 24.968C131.689 25.0747 131.684 25.192 131.673 25.32C131.662 25.4373 131.641 25.576 131.609 25.736H125.193V24.536H130.841L130.313 25C130.313 24.488 130.222 24.056 130.041 23.704C129.86 23.3413 129.604 23.064 129.273 22.872C128.942 22.6693 128.542 22.568 128.073 22.568C127.582 22.568 127.15 22.6747 126.777 22.888C126.404 23.1013 126.116 23.4 125.913 23.784C125.71 24.168 125.609 24.6213 125.609 25.144C125.609 25.6773 125.716 26.1467 125.929 26.552C126.142 26.9467 126.446 27.256 126.841 27.48C127.236 27.6933 127.689 27.8 128.201 27.8C128.628 27.8 129.017 27.7253 129.369 27.576C129.732 27.4267 130.041 27.2027 130.297 26.904L131.225 27.848C130.862 28.2747 130.414 28.6 129.881 28.824C129.358 29.048 128.798 29.16 128.201 29.16ZM136.491 29.16C135.798 29.16 135.168 28.9893 134.603 28.648C134.038 28.296 133.59 27.8213 133.259 27.224C132.939 26.6267 132.779 25.9547 132.779 25.208C132.779 24.4613 132.939 23.7893 133.259 23.192C133.59 22.5947 134.032 22.12 134.587 21.768C135.152 21.416 135.787 21.24 136.491 21.24C137.067 21.24 137.579 21.3627 138.027 21.608C138.486 21.8427 138.854 22.1733 139.131 22.6C139.408 23.016 139.563 23.5013 139.595 24.056V26.344C139.563 26.888 139.408 27.3733 139.131 27.8C138.864 28.2267 138.502 28.5627 138.043 28.808C137.595 29.0427 137.078 29.16 136.491 29.16ZM136.731 27.8C137.446 27.8 138.022 27.56 138.459 27.08C138.896 26.5893 139.115 25.9653 139.115 25.208C139.115 24.6853 139.014 24.232 138.811 23.848C138.619 23.4533 138.342 23.1493 137.979 22.936C137.616 22.712 137.195 22.6 136.715 22.6C136.235 22.6 135.808 22.712 135.435 22.936C135.072 23.16 134.784 23.4693 134.571 23.864C134.368 24.248 134.267 24.6907 134.267 25.192C134.267 25.704 134.368 26.1573 134.571 26.552C134.784 26.936 135.078 27.24 135.451 27.464C135.824 27.688 136.251 27.8 136.731 27.8ZM139.019 29V26.952L139.291 25.096L139.019 23.256V21.4H140.475V29H139.019ZM144.988 29.16C144.561 29.16 144.156 29.1067 143.772 29C143.398 28.8827 143.052 28.7227 142.732 28.52C142.412 28.3067 142.134 28.056 141.9 27.768L142.828 26.84C143.105 27.1813 143.425 27.4373 143.788 27.608C144.15 27.768 144.556 27.848 145.004 27.848C145.452 27.848 145.798 27.7733 146.044 27.624C146.289 27.464 146.412 27.2453 146.412 26.968C146.412 26.6907 146.31 26.4773 146.108 26.328C145.916 26.168 145.665 26.04 145.356 25.944C145.046 25.8373 144.716 25.736 144.364 25.64C144.022 25.5333 143.697 25.4 143.388 25.24C143.078 25.08 142.822 24.8613 142.62 24.584C142.428 24.3067 142.332 23.9387 142.332 23.48C142.332 23.0213 142.444 22.6267 142.668 22.296C142.892 21.9547 143.201 21.6933 143.596 21.512C144.001 21.3307 144.486 21.24 145.052 21.24C145.649 21.24 146.177 21.3467 146.636 21.56C147.105 21.7627 147.489 22.072 147.788 22.488L146.86 23.416C146.646 23.1387 146.38 22.9253 146.06 22.776C145.75 22.6267 145.398 22.552 145.004 22.552C144.588 22.552 144.268 22.6267 144.044 22.776C143.83 22.9147 143.724 23.112 143.724 23.368C143.724 23.624 143.82 23.8213 144.012 23.96C144.204 24.0987 144.454 24.216 144.764 24.312C145.084 24.408 145.414 24.5093 145.756 24.616C146.097 24.712 146.422 24.8453 146.732 25.016C147.041 25.1867 147.292 25.416 147.484 25.704C147.686 25.992 147.788 26.3707 147.788 26.84C147.788 27.5547 147.532 28.12 147.02 28.536C146.518 28.952 145.841 29.16 144.988 29.16ZM152.92 29.16C152.162 29.16 151.48 28.9893 150.872 28.648C150.264 28.296 149.784 27.8213 149.432 27.224C149.08 26.6267 148.904 25.9493 148.904 25.192C148.904 24.4453 149.074 23.7733 149.416 23.176C149.768 22.5787 150.237 22.1093 150.824 21.768C151.421 21.416 152.088 21.24 152.824 21.24C153.528 21.24 154.146 21.4 154.68 21.72C155.224 22.04 155.645 22.4827 155.944 23.048C156.253 23.6133 156.408 24.2533 156.408 24.968C156.408 25.0747 156.402 25.192 156.392 25.32C156.381 25.4373 156.36 25.576 156.328 25.736H149.912V24.536H155.56L155.032 25C155.032 24.488 154.941 24.056 154.76 23.704C154.578 23.3413 154.322 23.064 153.992 22.872C153.661 22.6693 153.261 22.568 152.792 22.568C152.301 22.568 151.869 22.6747 151.496 22.888C151.122 23.1013 150.834 23.4 150.632 23.784C150.429 24.168 150.328 24.6213 150.328 25.144C150.328 25.6773 150.434 26.1467 150.648 26.552C150.861 26.9467 151.165 27.256 151.56 27.48C151.954 27.6933 152.408 27.8 152.92 27.8C153.346 27.8 153.736 27.7253 154.088 27.576C154.45 27.4267 154.76 27.2027 155.016 26.904L155.944 27.848C155.581 28.2747 155.133 28.6 154.6 28.824C154.077 29.048 153.517 29.16 152.92 29.16Z" fill="white"/>
                <path d="M14.1588 24.638V22.685H26.8378V24.638H14.1588ZM19.4908 17.012H21.5058V30.28H19.4908V17.012Z" fill="white"/>
              </svg>
            </AddNewReleaseButton>
          </NoReleases>
        )}
         <PaginatorBar>
              <Paginator
                first={first}
                rows={pageCount}
                totalRecords={total}
                rowsPerPageOptions={[5, 10, 20, 30]}
                onPageChange={onPageChange}
                pageLinkSize={isMobile ? 1 : 5}
                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              />
            </PaginatorBar>
      </ItemsContainer>
      
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 13px 0px 13px;
  height: 100%;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 20px;
  white-space: nowrap;
`;

const ControlsBar = styled.div<{ $hidden: boolean }>`
  overflow: hidden;
  transform: ${p => (p.$hidden ? "translateY(-10px)" : "translateY(0)")};
  opacity: ${p => (p.$hidden ? 0 : 1)};
  max-height: ${p => (p.$hidden ? "0px" : "120px")};
  margin: ${p => (p.$hidden ? "0" : "0px 0 10px")};
  transition:
    max-height 240ms ease,
    opacity 180ms ease,
    transform 240ms ease,
    margin 240ms ease;
  will-change: max-height, opacity, transform, margin;
`;

const TitleFiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const FiltersWrapper = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const SearchInput = styled(Input)`
  height: 43px;
  max-width: 160px;
  min-width: 80px;
  overflow: hidden;
  flex: 0 0 auto;
`;

const NoReleases = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  flex: 1;
`;

const NoReleasesTitle = styled.div`
  font-weight: 400;
  font-size: 20px;
  line-height: 18px;
`;

const AddNewReleaseButton = styled(CreateReleaseButton)`
  width: 181px;
  height: 47px;
  padding: 0;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(103, 119, 255, 0.28);

  svg rect {
    fill: transparent;
  }
`;

const ItemsContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const Loader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0.5;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
`;

const DataContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow-y: auto;
`;

const BottomSpacer = styled.div`
  flex: 0 0 auto;
  height: 100px;
`;

const PaginatorBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  padding: 10px 0;
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  z-index: 5;

  .p-paginator {
    width: 100%;
    display: flex;
    justify-content: center;
    position: relative;
    box-sizing: border-box;
    /* reserve space for the rows-per-page dropdown so it doesn't cover ">>" */
    padding-right: 88px;
  }

  /* keep rows-per-page dropdown on the right */
  .p-paginator-rpp-options,
  .p-paginator .p-dropdown {
    position: absolute;
    right: 10px;
  }
`;
